<template>
  <layout-report-step
    :isDone="isDone"
    :title="configuration.header.title"
    :description="configuration.header.description"
    :image="configuration.header.image"
    :debug="configuration.header.debug"
    :hasInstructions="configuration.instruction ? true : false"
    :instructionPictures="configuration.instruction.pictures"
    :instructionText="configuration.instruction.text"
    :instructionTitle="configuration.instruction.title"
    :forceNextBtn="true"
  >
    <FileUploadMulti
      :instruction="configuration.instruction"
      :snackbarTitle="multiSteps.frontLeft.text"
      :example="multiSteps.frontLeft.example"
      :overlay="multiSteps.frontLeft.overlay"
      :text="multiSteps.frontLeft.text"
      :textAlt="multiSteps.frontLeft.textAlt"
      :filesCb="multiSteps.frontLeft.filesCb"
      :displayImages="getOverviewFrontLeft"
      :handleDeleteCb="multiSteps.frontLeft.handleDelete"
      :handleUploadCb="multiSteps.frontLeft.handleUpload"
      data-test-front-left-image-upload
    ></FileUploadMulti>
    <FileUploadMulti
      :example="multiSteps.frontRight.example"
      :instruction="configuration.instruction"
      :snackbarTitle="multiSteps.frontRight.text"
      :overlay="multiSteps.frontRight.overlay"
      :text="multiSteps.frontRight.text"
      :textAlt="multiSteps.frontRight.textAlt"
      :filesCb="multiSteps.frontRight.filesCb"
      :displayImages="getOverviewFrontRight"
      :handleDeleteCb="multiSteps.frontRight.handleDelete"
      :handleUploadCb="multiSteps.frontRight.handleUpload"
      data-test-front-right-image-upload
    ></FileUploadMulti>
    <FileUploadMulti
      :instruction="configuration.instruction"
      :snackbarTitle="multiSteps.rearRight.text"
      :example="multiSteps.rearRight.example"
      :overlay="multiSteps.rearRight.overlay"
      :text="multiSteps.rearRight.text"
      :textAlt="multiSteps.rearRight.textAlt"
      :filesCb="multiSteps.rearRight.filesCb"
      :displayImages="getOverviewRearRight"
      :handleDeleteCb="multiSteps.rearRight.handleDelete"
      :handleUploadCb="multiSteps.rearRight.handleUpload"
      data-test-rear-right-image-upload
    ></FileUploadMulti>
    <FileUploadMulti
      :instruction="configuration.instruction"
      :snackbarTitle="multiSteps.rearLeft.text"
      :example="multiSteps.rearLeft.example"
      :overlay="multiSteps.rearLeft.overlay"
      :text="multiSteps.rearLeft.text"
      :textAlt="multiSteps.rearLeft.textAlt"
      :filesCb="multiSteps.rearLeft.filesCb"
      :displayImages="getOverviewRearLeft"
      :handleDeleteCb="multiSteps.rearLeft.handleDelete"
      :handleUploadCb="multiSteps.rearLeft.handleUpload"
      data-test-rear-left-image-upload
    ></FileUploadMulti>
    <report-image-upload
      :example="multiSteps.numberPlate.example"
      :asset="multiSteps.numberPlate.overlay"
      :instruction="instruction"
      :text="multiSteps.numberPlate.text"
      :textAlt="multiSteps.numberPlate.textAlt"
      :type="tuev"
    />
    <report-image-upload
      :example="multiSteps.top.example"
      :asset="multiSteps.top.overlay"
      :instruction="instruction"
      :text="multiSteps.top.text"
      :textAlt="multiSteps.top.textAlt"
      :type="others"
    />

    <FileUploadMulti
      :example="multiSteps.cockpit.example"
      :instruction="configuration.instruction"
      :snackbarTitle="multiSteps.cockpit.text"
      :overlay="multiSteps.cockpit.overlay"
      :text="multiSteps.cockpit.text"
      :textAlt="multiSteps.cockpit.textAlt"
      :filesCb="multiSteps.cockpit.filesCb"
      :displayImages="getCockpit"
      :handleDeleteCb="multiSteps.cockpit.handleDelete"
      :handleUploadCb="multiSteps.cockpit.handleUpload"
      data-test-front-right-image-upload
    ></FileUploadMulti>
  </layout-report-step>
</template>

<script lang="ts">
import FileUploadMulti from "@/components/files/FileUploadMulti.vue";
import LayoutReportStep from "@/layouts/LayoutReportStep.vue";
import { AssetEnum } from "@/lib/AssetRepository";
import { IReportHeaderData } from "@/lib/interfaces/Report/IReportHeaderData";
import { IReportInstructionData } from "@/lib/interfaces/Report/IReportInstructionData";
import { IReportStep } from "@/lib/interfaces/Report/IViewReportStep";
import ReportStepMixin from "@/mixins/ReportStepMixin.vue";
import { IImageUploaded } from "@/models/Image/IImageUploaded";
import { ReportImageType } from "@/models/Report/ReportImageType";
import { ReportScreenEnum } from "@/store/enum/partner/report-screen.enum";
import { ReportModule } from "@/store/modules/report.store";
import { Component } from "vue-property-decorator";
import ReportImageUpload from "./ReportImageUpload.vue";

@Component({
  components: {
    LayoutReportStep,
    FileUploadMulti,
    ReportImageUpload
  }
})
export default class ReportImageOverviewAlt extends ReportStepMixin implements IReportStep {
  header: IReportHeaderData = this.configuration.header;
  instruction: IReportInstructionData = this.configuration.instruction;

  asset = AssetEnum.tuev;

  screen = ReportScreenEnum.overviewimagealt;

  get cockpit() {
    return ReportImageType.cockpit;
  }

  get others() {
    return ReportImageType.others;
  }

  get tuev() {
    return ReportImageType.tuev;
  }

  get multiSteps() {
    return {
      frontLeft: {
        example: this.configuration.exampleImages.frontLeft.example,
        overlay: this.configuration.exampleImages.frontLeft.overlay,
        text: "report.imageOverviewAlt.leftText",
        textAlt: "report.imageOverviewAlt.leftTextAlt",
        filesCb: ReportModule.overviewsFrontLeft,
        handleUpload: ReportModule.addOverviewFrontLeft,
        handleDelete: ReportModule.removeOverviewFrontLeft
      },
      rearLeft: {
        example: this.configuration.exampleImages.rearLeft.example,
        overlay: this.configuration.exampleImages.rearLeft.overlay,
        text: "report.imageOverviewAlt.rearText",
        textAlt: "report.imageOverviewAlt.rearTextAlt",
        filesCb: ReportModule.overviewsRearLeft,
        handleUpload: ReportModule.addOverviewRearLeft,
        handleDelete: ReportModule.removeOverviewRearLeft
      },
      rearRight: {
        example: this.configuration.exampleImages.rearRight.example,
        overlay: this.configuration.exampleImages.rearRight.overlay,
        text: "report.imageOverviewAlt.rightText",
        textAlt: "report.imageOverviewAlt.rightTextAlt",
        filesCb: ReportModule.overviewsRearRight,
        handleUpload: ReportModule.addOverviewRearRight,
        handleDelete: ReportModule.removeOverviewRearRight
      },
      frontRight: {
        example: this.configuration.exampleImages.frontRight.example,
        overlay: this.configuration.exampleImages.frontRight.overlay,
        text: "report.imageOverviewAlt.frontText",
        textAlt: "report.imageOverviewAlt.frontTextAlt",
        filesCb: ReportModule.overviewsFrontRight,
        handleUpload: ReportModule.addOverviewFrontRight,
        handleDelete: ReportModule.removeOverviewFrontRight
      },
      top: {
        example: AssetEnum.motorbikeTop,
        overlay: AssetEnum.motorbikeTopOutline,
        text: "report.imageOverviewAlt.topText",
        textAlt: "report.imageOverviewAlt.topTexteAlt",
        filesCb: ReportModule.cockpits,
        handleUpload: ReportModule.addCockpit,
        handleDelete: ReportModule.removeCockpit
      },
      numberPlate: {
        example: AssetEnum.numberplateMotorbike,
        overlay: AssetEnum.numberplateMotorbike,
        text: "report.imageOverviewAlt.numberplateText",
        textAlt: "report.imageOverviewAlt.numberplateTextAlt"
      },
      cockpit: {
        example: AssetEnum.cockpit,
        overlay: AssetEnum.cockpitOutline,
        text: "report.imageOverviewAlt.cockpitText",
        textAlt: "report.imageOverviewAlt.cockpitTextAlt",
        filesCb: ReportModule.cockpits,
        handleUpload: ReportModule.addCockpit,
        handleDelete: ReportModule.removeCockpit
      }
    };
  }
  get configuration() {
    return {
      exampleImages: {
        frontLeft: {
          example: AssetEnum.motorbikeLeft,
          overlay: AssetEnum.motorbikeLeftOutline
        },
        rearLeft: {
          example: AssetEnum.motorbikeRear,
          overlay: AssetEnum.motorbikeRearOutline
        },
        rearRight: {
          example: AssetEnum.motorbikeRight,
          overlay: AssetEnum.motorbikeRightOutline
        },
        frontRight: {
          example: AssetEnum.motorbikeFront,
          overlay: AssetEnum.motorbikeFrontOutline
        }
      },
      header: {
        title: "report.imageOverviewAlt.title",
        description: "report.imageOverviewAlt.description",
        image: AssetEnum.motorbikeFrontRight,
        debug: false
      },

      instruction: {
        title: "report.imageOverviewAlt.title",
        text: "report.imageOverviewAlt.instructionText",
        pictures: [AssetEnum.motorbikeFrontRight],
        example: AssetEnum.motorbikeFrontRight,
        display: true
      }
    };
  }

  get getOverviewFrontLeft() {
    return ReportModule.overviewsFrontLeft;
  }

  get getOverviewFrontRight() {
    return ReportModule.overviewsFrontRight;
  }

  get getOverviewRearLeft() {
    return ReportModule.overviewsRearLeft;
  }

  get getOverviewRearRight() {
    return ReportModule.overviewsRearRight;
  }

  get getCockpit(): IImageUploaded[] {
    return ReportModule.cockpits;
  }

  get getOthers(): IImageUploaded[] {
    return ReportModule.overviews.filter((image) => image.type === this.others);
  }

  /**
   * @inheritdoc
   */
  get isDone(): boolean {
    if (this.getOverviewFrontLeft.length <= 0) {
      return false;
    }

    if (this.getOverviewFrontRight.length <= 0) {
      return false;
    }

    if (this.getOverviewRearLeft.length <= 0) {
      return false;
    }

    if (this.getOverviewRearRight.length <= 0) {
      return false;
    }

    if (this.getOthers.length <= 0) {
      return false;
    }

    if (this.getCockpit.length <= 0) {
      return false;
    }

    return true;
  }
}
</script>

<style></style>
